import * as React from 'react';

import { cn } from '../../lib/stylingUtils';

export interface EmojiProps {
  /**
   * The emoji character or children containing the emoji
   * Examples: "👋", "😊", "🚀"
   */
  children?: string;
  /**
   * Optional custom aria-label for accessibility
   * If not provided, will be auto-generated from emoji
   */
  'aria-label'?: string;
  /**
   * Optional className for styling
   */
  className?: string;
}

/**
 * Emoji component that renders non-colored OpenMoji SVG icons
 *
 * Uses OpenMoji's black (non-colored) SVGs for consistent, styleable emoji rendering
 * Falls back to native Unicode emoji if SVG loading fails
 *
 * Size is inherited from container via currentColor/1em sizing
 *
 * @example
 * ```tsx
 * <Emoji>👋</Emoji>
 * <Emoji aria-label="waving hand">👋</Emoji>
 * ```
 */
export const Emoji: React.FC<EmojiProps> = ({
  children,
  'aria-label': ariaLabel,
  className,
}) => {
  const emoji = typeof children === 'string' ? children : '';

  // Generate aria-label from emoji if not provided
  const accessibilityLabel = React.useMemo(() => {
    if (ariaLabel) return ariaLabel;
    return `emoji ${emoji}`;
  }, [ariaLabel, emoji]);

  return (
    <span
      role="img"
      aria-label={accessibilityLabel}
      className={cn('@Emoji', className)}
    >
      {emoji}
    </span>
  );
};

Emoji.displayName = 'Emoji';
